/** Cookie-authenticated Monday bridge contract; no provider credentials reach the client. */
export const mondayFields = [
  'order_no',
  'order_type',
  'po_number',
  'customer_reference_id',
  'requested_pickup_date',
  'scheduled_date',
  'dispatch_date',
  'monday_status',
] as const;
export interface MondayBoardConfig {
  customer_bindings?: Record<
    string,
    { source_value: string; account_id: string }
  >;
  scan_interval_seconds?: number | null;
  whole_board?: boolean;
  create_after?: string | null;
  item_ids?: string[];
  customer_column_id?: string | null;
  customers?: Record<string, string>;
  assignment_column_id?: string | null;
  people?: Record<string, string>;
  board_id: string;
  facility_id: string;
  role: 'job_root' | 'job_related' | 'directory';
  root_board_id: string | null;
  relation_column_id: string | null;
  account_id: string | null;
  columns: Record<string, string>;
}
export interface MondayConfig {
  enabled: boolean;
  mode: 'preview' | 'apply';
  boards: MondayBoardConfig[];
}
export interface MondayStatus extends Omit<MondayConfig, 'boards'> {
  files?: { pending: number; ready: number; blocked: number };
  configured: boolean;
  can_configure: boolean;
  token_configured: boolean;
  boards: (MondayBoardConfig & {
    poll_interval_seconds?: number;
    accessible: boolean | null;
    last_scan_at: string | null;
    last_success_at: string | null;
    error: string | null;
  })[];
  counts: { pending: number; applied: number; blocked: number };
  issues_truncated?: boolean;
  issues: {
    board_id: string;
    item_id: string;
    state: string;
    reason: string;
    entity_id: string | null;
  }[];
}
export interface MondayItemSummary {
  board_id: string;
  item_id: string;
  state: string;
  reason: string | null;
  entity_id: string | null;
}
export interface MondayItemPage {
  items: MondayItemSummary[];
  next: string | null;
}
export interface MondaySourceDetail {
  attachments?: {
    byte_size?: number | null;
    sha256?: string | null;
    id: string;
    name: string;
    state: string;
    error: string | null;
  }[];
  item: {
    id: string;
    board_id: string;
    name: string;
    board_name?: string | null;
    updated_at: string | null;
    column_values: {
      id: string;
      text: string | null;
      type?: string;
      column?: { title: string } | null;
    }[];
    updates: {
      id: string;
      body: string;
      created_at: string;
      replies: { id: string; body: string }[];
    }[];
    assets: { id: string; name: string }[];
  };
  entity_id: string | null;
  state: string;
  reason: string | null;
}
export function mondayConfig(status: MondayStatus): MondayConfig {
  return {
    enabled: status.enabled,
    mode: status.mode,
    boards: status.boards.map(
      ({
        board_id,
        customer_bindings,
        scan_interval_seconds,
        whole_board,
        create_after,
        item_ids,
        facility_id,
        role,
        root_board_id,
        relation_column_id,
        account_id,
        columns,
        customer_column_id,
        customers,
        assignment_column_id,
        people,
      }) => ({
        customer_bindings: Object.fromEntries(
          Object.entries(customer_bindings ?? {}).map(([id, binding]) => [
            id,
            { ...binding },
          ])
        ),
        scan_interval_seconds: scan_interval_seconds ?? null,
        whole_board: whole_board ?? false,
        create_after: create_after ?? null,
        item_ids: [...(item_ids ?? [])],
        board_id,
        facility_id,
        role,
        root_board_id,
        relation_column_id,
        account_id,
        columns: { ...columns },
        customer_column_id: customer_column_id ?? null,
        customers: { ...customers },
        assignment_column_id: assignment_column_id ?? null,
        people: { ...people },
      })
    ),
  };
}

export interface MondayJobSources {
  sources: MondaySourceDetail[];
  enabled: boolean;
  mode: MondayConfig['mode'];
  truncated: boolean;
}

/** The copy engine's outcomes, in the server's display order (`copy_admin::COUNTED_OUTCOMES`). */
export const mondayCopyOutcomes = [
  'copied',
  'would_copy',
  'skipped_equal',
  'no_target',
  'multi_target',
  'condition_false',
  'refused',
  'error',
] as const;
export type MondayCopyOutcome = (typeof mondayCopyOutcomes)[number];
/** The copy-engine switches; a save sends only the ones it changes (the server keeps the rest). */
export interface MondayCopyConfig {
  copy_enabled?: boolean;
  copy_push?: boolean;
  copy_files?: boolean;
  copy_rule_push?: Record<string, boolean>;
}
export interface MondayCopyRule {
  id: string;
  source_board: string;
  source_column: string;
  target_board: string;
  target_column: string;
  kind: string;
  /** The rule's OWN switch; it writes only while the master `copy_push` is on too. */
  push: boolean;
  last_outcome: string | null;
  last_at: string | null;
  counts_24h: Partial<Record<MondayCopyOutcome, number>>;
}
export interface MondayCopyStatus {
  copy_enabled: boolean;
  copy_push: boolean;
  copy_files: boolean;
  copy_route_mounted: boolean;
  public_base_set: boolean;
  hints: { pending: number; blocked: number };
  webhooks: { owned: number };
  rules: MondayCopyRule[];
  /** The server's verdict: may this reader flip the switches, run and register (the grant-only
   *  copy permission or a super admin, on a signed-in session). The screen never re-derives it. */
  can_operate: boolean;
}
export interface MondayCopyLogRow {
  id: number;
  at: string;
  rule_id: string | null;
  source_board: string | null;
  source_item: string | null;
  target_board: string | null;
  target_item: string | null;
  column_id: string | null;
  before: unknown;
  after: unknown;
  outcome: string;
  error: string | null;
  request_id: string | null;
}
export interface MondayCopyLog {
  limit: number;
  rows: MondayCopyLogRow[];
}
export interface MondayCopyLogQuery {
  rule?: string;
  outcome?: string;
  limit?: number;
}
export interface MondayCopyRunReport {
  enabled: boolean;
  hints: number;
  completed: number;
  retained: number;
  stopped: string | null;
  outcomes: Partial<Record<MondayCopyOutcome, number>>;
}
export interface MondayCopyWebhookReport {
  created: number;
  recreated: number;
  kept: number;
  deleted: number;
  stale: number;
  foreign: number;
}
/** What the copy engine (or one rule) does: nothing, read-and-log only, or write to monday. */
export type MondayCopyMode = 'off' | 'dry_run' | 'live' | 'files_off';
export function mondayCopyEngineMode(status: MondayCopyStatus): MondayCopyMode {
  if (!status.copy_enabled) return 'off';
  return status.copy_push ? 'live' : 'dry_run';
}
/** A rule writes only while the engine, the master push AND the rule's own push are all on.
 *  A `files` rule also needs `copy_files`: without it the server refuses the rule outright
 *  (`files_not_enabled`), so it is neither a dry run nor live. */
export function mondayCopyRuleMode(
  status: MondayCopyStatus,
  rule: MondayCopyRule
): MondayCopyMode {
  if (!status.copy_enabled) return 'off';
  if (rule.kind === 'files' && !status.copy_files) return 'files_off';
  return status.copy_push && rule.push ? 'live' : 'dry_run';
}
/** The log query string; empty filters are omitted (the server reads them as "no filter"). */
export function mondayCopyLogParams(query: MondayCopyLogQuery): string {
  const params = new URLSearchParams();
  if (query.rule) params.set('rule', query.rule);
  if (query.outcome) params.set('outcome', query.outcome);
  if (query.limit) params.set('limit', String(query.limit));
  return params.toString();
}
